/**
 * SiteWorkspaceDrawer — 현장 작업물 슬라이드오버 (스튜디오 개선 설계서 FR-11~FR-16).
 *
 * 현장 하나를 골라 공사스케줄/사진첩/나의 할일 3탭으로 그 현장 작업물만 바로 들여다본다.
 * iframe 없이 각 에이전트 화면 컴포넌트를 그대로 마운트한다(§11 컴포넌트 마운트).
 * (2026-10-06 개선사항 B — 시공사례 탭 제거, 공사스케줄→사진첩→나의 할일 순으로 재정렬.
 * 시공사례는 "내 프로필 홍보"·"나의 시공사례"에서 계속 볼 수 있어 여기서만 뺐다.)
 *
 * 진행 상태:
 *  - 공사스케줄(scheduler)    — SchedulerSiteEmbed로 마운트. SchedulerPage 자체(전체 라우트
 *    소유·URL 직접 변경·회사 전체 현장 전환 패널·생성/설정/공유 플로우)를 그대로 끼워 넣는
 *    대신, "스케줄 관리" 뷰 4종(간트/캘린더/리스트/공종수정)만 로컬 state로 전환하는 전용
 *    임베드를 새로 만들어(SchedulerSiteEmbed.tsx) 위험을 줄였다. 탭 라벨은 호출부(SiteList)가
 *    현장 상태에 따라 "진행현황"/"완료현황"으로 넘겨준다(scheduleTabLabel).
 *  - 사진첩(photo-organizer)  — SiteDetailMain을 embedded 모드로 마운트, 완성.
 *  - 나의 할일(todo)          — TodoBoard(embedded)로 이 현장 할 일만 표시(오늘 · 7일). 탭은 라우트 이동 없이 로컬 상태.
 *
 * 기본 폭은 기획서 현장 작업공간 슬라이드오버(.sw-panel)와 동일하게 min(1040px, 82vw) —
 * 공사스케줄 탭의 간트 툴바(선후관계·반장보기·발주·입고·대기일·범위·보기 등)가 한 줄에
 * 들어가려면 이 정도 폭이 필요하다(720px에선 줄바꿈되어 깨져 보였다).
 *
 * FR-13 "크게보기": Drawer의 maximizable로 폭을 그 기본폭 ↔ (viewport - LeftNav 폭)으로 토글한다.
 * LeftNav 폭은 rail/expanded·서브패널 고정 여부에 따라 실시간으로 바뀌므로 appStore.navWidth를
 * 구독한다(LeftNav.tsx가 발행). FR-16 브레드크럼: 이 드로어는 라우트 이동 없이 열리는 오버레이라
 * TopBar의 pathname 기반 계산으로는 표현이 안 돼, 열려있는 동안 appStore.breadcrumbOverride로
 * "나의 현장 › [현장명] › {탭}"을 덮어쓰고 닫히면(언마운트) 되돌린다.
 *
 * 공사스케줄 탭의 저장 안 한 변경사항: SchedulerPage는 useBlocker(라우트 이동 차단)로 이걸
 * 지키지만, 이 드로어는 닫혀도 라우트가 안 바뀌어 useBlocker가 개입할 지점이 없다. 그래서
 * onDirtyChange로 직접 dirty 여부를 받아, 닫기 시도 시 이 컴포넌트가 자체적으로 확인창을 띄운다.
 */
import { useEffect, useState } from 'react';
import { Drawer, Tabs, Modal, Button, EmptyState } from '@indipia/ui-core';
import { useAppStore } from '@indipia/shared';
import { SiteDetailMain } from '@indipia/agents-photo-organizer';
import { SchedulerSiteEmbed, type DirtyHandle } from '@indipia/agents-scheduler';
import { SiteWorkFeed } from '@indipia/agents-work-log';
import { TodoBoard, type TodoTab } from '../../shared/todo/TodoBoard';

// 2026-10-06 개선사항 B — 시공사례 탭 제거, 공사스케줄→사진첩→나의 할일 순으로 재정렬.
// '나의 할일'은 아직 실제 기능이 없어 임시 플레이스홀더 탭으로 둔다(사용자 확인 완료).
// 2026-10-16 940 S0-4(6)(7) — 나의 할일·진행현황·사진첩·공사현황 4탭. 공사현황 본문 = work-log SiteWorkFeed(REQ-11-07).
export type WorkspaceTab = 'todo' | 'schedule' | 'photos' | 'work';

export interface SiteWorkspaceDrawerProps {
  siteId: number;
  siteName: string;
  /** 현장 상태 — 나의 할일 빈 상태 문구 분기(거래완료). */
  siteStatus?: string;
  /** 공사스케줄 탭 라벨 — 현장 상태에 따라 호출부(SiteList)가 "진행현황"/"완료현황"을 넘긴다. */
  scheduleTabLabel?: string;
  initialTab?: WorkspaceTab;
  onClose: () => void;
}

const TAB_LABEL: Record<WorkspaceTab, string> = { schedule: '진행현황', photos: '사진첩', todo: '나의 할일', work: '공사현황' };

export function SiteWorkspaceDrawer({
  siteId, siteName, siteStatus, scheduleTabLabel, initialTab = 'schedule', onClose,
}: SiteWorkspaceDrawerProps) {
  const [tab, setTab] = useState<WorkspaceTab>(initialTab);
  const [todoTab, setTodoTab] = useState<TodoTab>('today');
  const navWidth = useAppStore((s) => s.navWidth);
  const setBreadcrumbOverride = useAppStore((s) => s.setBreadcrumbOverride);

  // 2026-10-06 개선사항 B: 공사스케줄 탭 라벨만 현장 상태(진행현황/완료현황)에 따라 동적 —
  // 나머지는 TAB_LABEL 고정값 그대로.
  const tabs: { label: string; value: WorkspaceTab }[] = [
    { label: TAB_LABEL.todo,   value: 'todo' },
    { label: scheduleTabLabel ?? TAB_LABEL.schedule, value: 'schedule' },
    { label: TAB_LABEL.photos, value: 'photos' },
    { label: TAB_LABEL.work,   value: 'work' },
  ];
  const currentTabLabel = tab === 'schedule' ? (scheduleTabLabel ?? TAB_LABEL.schedule) : TAB_LABEL[tab];

  // FR-16 — 열려있는 동안 "나의 현장 › [현장명] › {탭}"으로 브레드크럼을 덮어쓰고,
  // 닫히면(언마운트) 라우트 기반 기본 브레드크럼으로 되돌린다. 라우트 이동이 없는
  // 오버레이라 TopBar의 pathname 기반 계산만으로는 이 컨텍스트를 표현할 수 없다.
  useEffect(() => {
    setBreadcrumbOverride(['나의 현장', siteName, currentTabLabel]);
    return () => setBreadcrumbOverride(null);
  }, [siteName, currentTabLabel, setBreadcrumbOverride]);

  // 공사스케줄 탭의 저장 안 한 변경사항 가드 — SchedulerPage의 guardedAction과 동일한
  // 패턴(라우트 이동이 아니라 이 드로어 안에서의 탭 전환·닫기이므로 useBlocker 대신 직접
  // 감싼다). dirty 상태에서 다른 탭 클릭·닫기(×·배경 클릭·Esc) 시도 시 확인창을 띄운다.
  const [dirty, setDirty] = useState<DirtyHandle | null>(null);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const [savingDirty, setSavingDirty] = useState(false);

  const guardedAction = (action: () => void) => {
    if (dirty && dirty.count > 0) setPendingAction(() => action);
    else action();
  };
  const handleGuardedClose = () => guardedAction(onClose);
  const handleTabChange = (v: string) => guardedAction(() => setTab(v as WorkspaceTab));

  const handleDirtySave = async () => {
    if (!dirty) { pendingAction?.(); setPendingAction(null); return; }
    setSavingDirty(true);
    try {
      await dirty.save();
      setDirty(null);
      pendingAction?.();
      setPendingAction(null);
    } finally {
      setSavingDirty(false);
    }
  };
  const handleDirtyDiscard = () => {
    dirty?.discard();
    setDirty(null);
    pendingAction?.();
    setPendingAction(null);
  };

  return (
    <>
      <Drawer
        open
        onClose={handleGuardedClose}
        width="min(1040px, 82vw)"
        maximizable
        maximizedWidth={`calc(100vw - ${navWidth}px)`}
        className="rounded-l-xl"
      >
        {/* 헤더 */}
        <div className="flex shrink-0 items-center justify-between border-b border-border px-5 py-3.5">
          <div className="min-w-0 flex-1 truncate text-[15px] font-bold text-text">{siteName}</div>
          <button
            type="button"
            onClick={handleGuardedClose}
            aria-label="닫기"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-title-sm leading-none text-text-muted hover:bg-surface-subtle hover:text-text"
          >
            ×
          </button>
        </div>

        {/* 탭 */}
        <div className="shrink-0 border-b border-border px-3">
          <Tabs variant="line" tabs={tabs} value={tab} onChange={handleTabChange} />
        </div>

        {/* 본문 — 탭마다 마운트/언마운트(동시 마운트 시 배경 조회 경합 방지), 현장이 바뀌면
            key로 강제 재마운트되도록 상위(호출부)에서 이 컴포넌트 자체를 siteId로 key를 줄 것. */}
        <div className="flex min-h-0 flex-1 overflow-hidden">
          {tab === 'photos' && (
            <SiteDetailMain siteId={siteId} embedded onClose={onClose} />
          )}
          {tab === 'todo' && (
            // '나의 할일' — 거래완료 현장은 빈 상태, 그 외는 이 현장 할 일(940 S14-6(2)·(3)).
            siteStatus === 'DONE' ? (
              <div className="flex w-full flex-1 items-center justify-center">
                <EmptyState title="거래완료된 현장이라 처리할 일이 없어요" />
              </div>
            ) : (
              <div className="w-full overflow-y-auto">
                <TodoBoard embedded siteId={siteId} siteName={siteName} tab={todoTab} onTabChange={setTodoTab} onBeforeNavigate={onClose} />
              </div>
            )
          )}
          {tab === 'work' && (
            // '공사현황' — 이 현장의 작업요청–작업보고 목록(REQ-11-07). 거래완료 현장은 열람 전용(사진 담기 숨김, 001 V2.6).
            <SiteWorkFeed key={siteId} siteId={siteId} readOnly={siteStatus === 'DONE'} />
          )}
          {tab === 'schedule' && (
            <div className="w-full overflow-y-auto">
              <SchedulerSiteEmbed
                siteId={siteId}
                onDirtyChange={setDirty}
                // 나의 할일·작업보고확인으로 나갈 때는 드로어를 닫고 이동한다(저장 안 한 변경사항이 있으면 확인 후)
                navigateAway={go => guardedAction(() => { onClose(); go(); })}
                // 신호등 기본 ON(빨강·노랑만)은 '진행현황' 탭 라벨일 때만 — 완료현황(거래완료)은 꺼짐
                signalDefaultOn={(scheduleTabLabel ?? TAB_LABEL.schedule) === TAB_LABEL.schedule}
              />
            </div>
          )}
        </div>
      </Drawer>

      {/* 공사스케줄 탭 저장 안 한 변경사항 확인 — SchedulerPage의 동명 확인창과 동일 문구/구성 */}
      <Modal
        open={pendingAction !== null}
        onClose={() => setPendingAction(null)}
        title="저장하지 않은 변경사항이 있습니다"
        footerAlign="stretch"
        footer={
          <>
            <Button
              variant="secondary"
              style={{ borderColor: 'var(--color-error)', color: 'var(--color-error)' }}
              onClick={handleDirtyDiscard}
            >저장 안 함</Button>
            <Button variant="primary" loading={savingDirty} onClick={handleDirtySave}>
              {savingDirty ? '저장 중...' : '저장'}
            </Button>
          </>
        }
      >
        <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)', lineHeight: 1.6 }}>
          날짜가 변경된 공종{dirty ? ` ${dirty.count}개` : ''}가 아직 저장되지 않았습니다. 저장하고 이동할까요?
        </div>
      </Modal>
    </>
  );
}
